<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import { useBranding } from 'shared/composables/useBranding';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import IntegrationEmptyState from './IntegrationEmptyState.vue';
import IntegrationConnectionCard from './IntegrationConnectionCard.vue';
import NewHook from '../NewHook.vue';

const props = defineProps({
  integrationId: { type: String, required: true },
  headerTitle: { type: String, required: true },
  emptyTitle: { type: String, required: true },
  emptyDescription: { type: String, default: '' },
  connectButtonLabel: { type: String, required: true },
  disconnectButtonLabel: { type: String, required: true },
  connectedTitle: { type: String, required: true },
  connectedDescription: { type: String, default: '' },
  disconnectConfirm: { type: String, required: true },
  icon: { type: String, default: 'i-lucide-plug' },
  iconClass: {
    type: String,
    default: 'bg-gradient-to-br from-n-brand to-n-indigo-600',
  },
  logoFile: { type: String, default: '' },
});

const store = useStore();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const showConnectModal = ref(false);
const showDeleteConfirmation = ref(false);

const { integration, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const uiFlags = computed(
  () => store.getters['integrations/getUIFlags'] || {}
);
const isLoading = computed(() => uiFlags.value.isFetching);
const hook = computed(() => integration.value?.hooks?.[0] || null);
const logoSrc = computed(
  () =>
    props.logoFile ||
    `/dashboard/images/integrations/${props.integrationId}.png`
);

const visibleSettings = computed(() => {
  const visible = integration.value?.visible_properties || [];
  const settings = hook.value?.settings || {};
  return visible
    .filter(key => settings[key] !== undefined && settings[key] !== '')
    .map(key => ({ key, value: String(settings[key]) }));
});

onMounted(() => {
  store.dispatch('integrations/get');
});

const openConnectModal = () => {
  showConnectModal.value = true;
};

const hideConnectModal = () => {
  showConnectModal.value = false;
};

const openDisconnect = () => {
  showDeleteConfirmation.value = true;
};

const closeDisconnect = () => {
  showDeleteConfirmation.value = false;
};

const confirmDisconnect = async () => {
  if (!hook.value) return;
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: hook.value.id,
      appId: hook.value.app_id,
    });
    useAlert(t('INTEGRATION_APPS.DELETE.API.SUCCESS_MESSAGE'));
    closeDisconnect();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const handleEdit = async () => {
  if (!hook.value) {
    openConnectModal();
    return;
  }
  // eslint-disable-next-line no-alert
  if (!window.confirm(props.disconnectConfirm)) return;
  try {
    await store.dispatch('integrations/deleteHook', {
      hookId: hook.value.id,
      appId: hook.value.app_id,
    });
    openConnectModal();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ||
        t('INTEGRATION_APPS.DELETE.API.ERROR_MESSAGE')
    );
  }
};

const headerAction = () => {
  if (hasConnectedHooks.value) {
    openDisconnect();
  } else {
    openConnectModal();
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="headerTitle"
    :button-label="
      hasConnectedHooks ? disconnectButtonLabel : connectButtonLabel
    "
    @click="headerAction"
  >
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <IntegrationEmptyState
      v-else-if="!hasConnectedHooks"
      :icon="icon"
      :title="emptyTitle"
      :description="replaceInstallationName(emptyDescription)"
      :button-label="connectButtonLabel"
      @action="openConnectModal"
    />

    <div v-else class="space-y-4">
      <IntegrationConnectionCard
        :icon="icon"
        :icon-class="iconClass"
        :title="connectedTitle"
        :description="connectedDescription"
      >
        <template v-if="logoFile || integrationId" #avatar>
          <div
            class="mr-4 flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-n-weak bg-n-alpha-3"
          >
            <img
              :src="logoSrc"
              :alt="headerTitle"
              class="h-full w-full object-contain"
            />
          </div>
        </template>
        <template #meta>
          <dl
            v-if="visibleSettings.length"
            class="mt-3 flex flex-col gap-1 text-sm"
          >
            <div
              v-for="item in visibleSettings"
              :key="item.key"
              class="flex gap-2 text-slate-600 dark:text-slate-400"
            >
              <dt class="font-medium capitalize text-slate-700 dark:text-slate-300">
                {{ item.key }}:
              </dt>
              <dd class="truncate">{{ item.value }}</dd>
            </div>
          </dl>
        </template>
        <template #actions>
          <Button
            slate
            faded
            size="sm"
            :label="t('INTEGRATION_SETTINGS.SHARED.EDIT_SETTINGS')"
            @click="handleEdit"
          />
        </template>
      </IntegrationConnectionCard>
    </div>

    <woot-modal v-model:show="showConnectModal" :on-close="hideConnectModal">
      <NewHook :integration-id="integrationId" @close="hideConnectModal" />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmation"
      :on-close="closeDisconnect"
      :on-confirm="confirmDisconnect"
      :title="t('INTEGRATION_APPS.DELETE.TITLE.ACCOUNT')"
      :message="t('INTEGRATION_APPS.DELETE.MESSAGE.ACCOUNT')"
      :confirm-text="t('INTEGRATION_APPS.DELETE.CONFIRM_BUTTON_TEXT.ACCOUNT')"
      :reject-text="t('INTEGRATION_APPS.DELETE.CANCEL_BUTTON_TEXT')"
    />
  </SettingsLayout>
</template>
